import { Button, ButtonIcon, cn } from "@netlify/ui";
import type { ReactElement } from "react";
import React, { useState } from "react";

const FEEDBACK_TIMEOUT_IN_MILLIS = 2000;

export const ClipboardWrapperCN = (
  icon: boolean,
  className: string | undefined,
) =>
  cn(
    "tw-relative",
    "tw-inline-block",
    icon ? "tw-w-auto" : "tw-w-full md:tw-w-auto",
    className,
  );

const ClipboardAlertCN = ({ feedback }: { feedback: string | null }) =>
  cn(
    "tw-block",
    "tw-absolute",
    "tw-bg-black",
    "tw-text-white",
    "tw-p-2",
    "tw-rounded-sm",
    "tw-text-xs",
    "tw-top-0",
    "tw-left-1/2",
    "tw-opacity-0",
    "tw-w-max",
    "dark:tw-border",
    "dark:tw-border-gray-dark",
    feedback && "tw-animate-pop-up",
  );

export const feedbackPopup = (feedback: string | null) => {
  return (
    <div role="alert" className={ClipboardAlertCN({ feedback })}>
      {feedback}
    </div>
  );
};

export interface ClipboardButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: boolean;
  link?: string | ReactElement;
  value: string;
  title?: string;
  tooltip?: string;
  onClick?: () => void;
  className?: string;
  /**
   * @default "secondary"
   */
  level?: "primary" | "secondary" | "tertiary" | undefined;
}

const ClipboardButton = ({
  disabled = false,
  icon = true,
  link,
  value,
  title = "Copy to clipboard",
  tooltip = "Copied!",
  onClick,
  className,
  level = "secondary",
}: ClipboardButtonProps) => {
  const [feedback, setFeedback] = useState<string | null>(null);

  const handleSuccess = () => {
    setFeedback(tooltip);
    setTimeout(() => {
      setFeedback(null);
    }, FEEDBACK_TIMEOUT_IN_MILLIS);
  };

  const handleError = () => {
    setFeedback("Couldn't copy text to clipboard");
  };

  const handleClick = () => {
    if (!value) {
      return;
    }

    void (async () => {
      try {
        await navigator.clipboard.writeText(value);
        if (onClick) {
          onClick();
        }
        handleSuccess();
      } catch {
        handleError();
      }
    })();
  };

  return (
    <div className={ClipboardWrapperCN(icon, className)}>
      {feedback && feedbackPopup(feedback)}
      {icon ? (
        <ButtonIcon
          disabled={disabled}
          level={level}
          iconName="copy-clipboard"
          onClick={handleClick}
          title={title}
        />
      ) : link ? (
        <button
          className="link"
          disabled={disabled}
          onClick={handleClick}
          title={title}
        >
          {link}
        </button>
      ) : (
        <Button disabled={disabled} level={level} onClick={handleClick}>
          {title}
        </Button>
      )}
    </div>
  );
};

export default ClipboardButton;
